export const FAMILY_CASH_BANK_LOG_PAGE_SIZE = 50;

export const ZERO_ADDRESS = "0x0000000000000000000000000000000000000000";

export type FamilyCashBankLogEntry = {
  logType: number;
  logSubType: string;
  user: string;
  recipient: string;
  amount: number;
  secondaryAmount: number;
  rank: number;
  timestamp: number;
};

/** Matches `MafiaFamilyCashBank.LogType` on-chain. */
export enum FamilyCashBankLogType {
  Deposit = 0,
  Withdraw = 1,
  Promotion = 2,
  Tax = 3,
  NativeTax = 4,
  NativeWithdraw = 5,
  NativeSwap = 6,
  Seed = 7,
}

export const FAMILY_CASH_BANK_LOG_TYPE_LABEL: Record<FamilyCashBankLogType, string> = {
  [FamilyCashBankLogType.Deposit]: "Deposit",
  [FamilyCashBankLogType.Withdraw]: "Withdraw",
  [FamilyCashBankLogType.Promotion]: "Promotion",
  [FamilyCashBankLogType.Tax]: "Tax",
  [FamilyCashBankLogType.NativeTax]: "Native tax",
  [FamilyCashBankLogType.NativeWithdraw]: "Native withdraw",
  [FamilyCashBankLogType.NativeSwap]: "Native swap",
  [FamilyCashBankLogType.Seed]: "Seed",
};

/** Human-readable labels for on-chain `logSubType` strings (e.g. "Crime", "SellBooze"). */
export const FAMILY_CASH_BANK_LOG_SUB_TYPE_LABEL: Record<string, string> = {
  Crime: "Crime",
  SellCar: "Car sale",
  SellBooze: "Booze sale",
  SellNarcs: "Narcs sale",
  HelperBot: "Helper bot",
  BuyKey: "Buy key",
};

export const FAMILY_CASH_BANK_CASH_LOG_TYPES = new Set<number>([
  FamilyCashBankLogType.Deposit,
  FamilyCashBankLogType.Withdraw,
  FamilyCashBankLogType.Promotion,
  FamilyCashBankLogType.Tax,
  FamilyCashBankLogType.NativeSwap,
  FamilyCashBankLogType.Seed,
]);

export const FAMILY_CASH_BANK_NATIVE_LOG_TYPES = new Set<number>([
  FamilyCashBankLogType.NativeTax,
  FamilyCashBankLogType.NativeWithdraw,
  FamilyCashBankLogType.NativeSwap,
]);

/** @deprecated Use `FAMILY_CASH_BANK_NATIVE_LOG_TYPES`. */
export const FAMILY_CASH_BANK_USD_LOG_TYPES = FAMILY_CASH_BANK_NATIVE_LOG_TYPES;

export function isNativeFamilyCashBankLogType(logType: number): boolean {
  return FAMILY_CASH_BANK_NATIVE_LOG_TYPES.has(logType);
}

export function formatFamilyCashBankLogSubType(logSubType: string): string {
  if (!logSubType) return "";
  return FAMILY_CASH_BANK_LOG_SUB_TYPE_LABEL[logSubType] ?? logSubType;
}

export function getFamilyCashBankLogDisplayLabel(
  logType: number,
  logSubType = ""
): string {
  const base =
    FAMILY_CASH_BANK_LOG_TYPE_LABEL[logType as FamilyCashBankLogType] ??
    `Type ${logType}`;

  if (
    logType === FamilyCashBankLogType.Tax ||
    logType === FamilyCashBankLogType.NativeTax
  ) {
    const subLabel = formatFamilyCashBankLogSubType(logSubType);
    return subLabel ? `${base} · ${subLabel}` : base;
  }

  return base;
}

export function getFamilyCashBankLogFilter(
  tab: "cash" | "native"
): Set<number> {
  return tab === "native"
    ? FAMILY_CASH_BANK_NATIVE_LOG_TYPES
    : FAMILY_CASH_BANK_CASH_LOG_TYPES;
}

export function filterFamilyCashBankLogs(
  logs: FamilyCashBankLogEntry[],
  logFilter: Set<number>
): FamilyCashBankLogEntry[] {
  return logs.filter((log) => logFilter.has(log.logType)).reverse();
}

export function paginateFamilyCashBankLogs(
  logs: FamilyCashBankLogEntry[],
  pageIndex: number,
  pageSize = FAMILY_CASH_BANK_LOG_PAGE_SIZE
): {
  pageLogs: FamilyCashBankLogEntry[];
  totalPages: number;
  pageIndex: number;
} {
  if (logs.length === 0) {
    return { pageLogs: [], totalPages: 0, pageIndex: 0 };
  }

  const totalPages = Math.ceil(logs.length / pageSize);
  const safePage = Math.max(0, Math.min(pageIndex, totalPages - 1));

  return {
    pageLogs: logs.slice(safePage * pageSize, safePage * pageSize + pageSize),
    totalPages,
    pageIndex: safePage,
  };
}

export function formatFamilyCashBankNativeAmount(amount: number): string {
  return amount.toLocaleString(undefined, { maximumFractionDigits: 6 });
}

export function formatFamilyCashBankLogAmount(
  log: FamilyCashBankLogEntry,
  formatCash: (value: number) => string
): {
  text: string;
  showCashIcon: boolean;
  showNativeIcon: boolean;
  isNativeAmount: boolean;
} {
  if (log.logType === FamilyCashBankLogType.NativeSwap) {
    return {
      text: `${formatFamilyCashBankNativeAmount(log.amount)} → ${formatCash(Math.floor(log.secondaryAmount))}`,
      showCashIcon: true,
      showNativeIcon: true,
      isNativeAmount: true,
    };
  }

  if (isNativeFamilyCashBankLogType(log.logType)) {
    return {
      text: formatFamilyCashBankNativeAmount(log.amount),
      showCashIcon: false,
      showNativeIcon: true,
      isNativeAmount: true,
    };
  }

  return {
    text: formatCash(Math.floor(log.amount)),
    showCashIcon: true,
    showNativeIcon: false,
    isNativeAmount: false,
  };
}

export function getFamilyCashBankLogAmountTitle(
  log: FamilyCashBankLogEntry,
  formatCash: (value: number) => string
): string | undefined {
  if (
    log.logType === FamilyCashBankLogType.NativeWithdraw &&
    log.recipient &&
    log.recipient !== ZERO_ADDRESS
  ) {
    return `Sent to ${log.recipient}`;
  }

  if (log.logType === FamilyCashBankLogType.NativeSwap && log.secondaryAmount > 0) {
    return `Swapped ${formatFamilyCashBankNativeAmount(log.amount)} native for ${formatCash(Math.floor(log.secondaryAmount))} cash`;
  }

  if (log.logType === FamilyCashBankLogType.Seed) {
    return `Owner seeded ${formatCash(Math.floor(log.amount))} cash into the family bank`;
  }

  if (
    (log.logType === FamilyCashBankLogType.Tax ||
      log.logType === FamilyCashBankLogType.NativeTax) &&
    log.logSubType
  ) {
    return `${getFamilyCashBankLogDisplayLabel(log.logType, log.logSubType)} from ${log.user}`;
  }

  return undefined;
}

export function getFamilyCashBankLogTypeBadgeText(
  log: FamilyCashBankLogEntry,
  rankLabel: string
): string {
  if (log.logType === FamilyCashBankLogType.Promotion && rankLabel) {
    return `Prom·${rankLabel}`;
  }

  return getFamilyCashBankLogDisplayLabel(log.logType, log.logSubType);
}

/** Page 0 = newest logs. Returns contract `start` / `length` for getFamilyLogs. */
export function getFamilyCashBankLogPageRange(
  totalCount: number,
  pageIndex: number,
  pageSize = FAMILY_CASH_BANK_LOG_PAGE_SIZE
): { start: number; length: number; totalPages: number; pageIndex: number } {
  if (totalCount <= 0) {
    return { start: 0, length: 0, totalPages: 0, pageIndex: 0 };
  }
  const totalPages = Math.ceil(totalCount / pageSize);
  const safePage = Math.max(0, Math.min(pageIndex, totalPages - 1));
  const end = totalCount - safePage * pageSize;
  const start = Math.max(0, end - pageSize);
  return {
    start,
    length: end - start,
    totalPages,
    pageIndex: safePage,
  };
}
